import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import axios from "../../../api/axios";
import {
  buildActivityGroupSections,
  filterActivityGroupSections,
  hasRealActivityGroups,
} from "../../utils/moduleSelectors/projectActivityGroupUtils";
import { useProjectActivityPickerData } from "../../utils/moduleSelectors/useProjectActivityPickerData";

// Promang design tokens (kept local so the pickers match the form 1:1)
const ACCENT = {
  solid: "#1463C7",
  grad: "linear-gradient(135deg,#1463C7,#2D8BE8)",
  soft: "#EAF3FD",
};

const AVATAR_COLORS = ["#4F46E5", "#0D9488", "#D946EF", "#F59E0B", "#0EA5E9", "#10B981", "#1463C7"];
export const avatarColor = (key) => {
  const s = String(key ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
export const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const SHEET_KEYFRAMES = `
@keyframes hfSheetIn{from{opacity:0}to{opacity:1}}
@keyframes hfSheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
`;

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ACCENT.solid} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

/** Bottom sheet on mobile; centered modal on wider screens. Rendered in a portal. */
export const BottomSheet = ({ open, onClose, title, searchValue, onSearch, searchPlaceholder, children }) => {
  const [isWide, setIsWide] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(min-width: 640px)");
    const update = () => setIsWide(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  if (!open) return null;
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(15,23,42,.45)",
        display: "flex",
        flexDirection: "column",
        justifyContent: isWide ? "center" : "flex-end",
        padding: isWide ? 24 : 0,
        animation: "hfSheetIn .2s ease",
        fontFamily: "'Plus Jakarta Sans',system-ui,-apple-system,sans-serif",
      }}
    >
      <style>{SHEET_KEYFRAMES}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: isWide ? 24 : "24px 24px 0 0",
          maxHeight: isWide ? "80%" : "82%",
          width: "100%",
          maxWidth: 520,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          animation: isWide ? "hfSheetIn .22s ease" : "hfSheetUp .3s cubic-bezier(.22,1,.36,1)",
          boxShadow: isWide ? "0 30px 70px -20px rgba(15,23,42,.45)" : "0 -10px 40px rgba(15,23,42,.2)",
        }}
      >
        <div style={{ padding: "18px 18px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", flex: "none" }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: "#0F172A" }}>{title}</span>
          <button
            type="button"
            onClick={onClose}
            style={{ width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", border: "none", background: "#F1F5F9", borderRadius: 10, cursor: "pointer", color: "#475569" }}
          >
            <CloseIcon />
          </button>
        </div>

        {onSearch && (
          <div style={{ padding: "0 18px 12px", flex: "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#F1F5F9", borderRadius: 12, padding: "11px 14px" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                value={searchValue}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                style={{ flex: 1, border: "none", background: "transparent", outline: "none", fontSize: 15, color: "#0F172A", fontFamily: "inherit" }}
              />
            </div>
          </div>
        )}

        <div style={{ overflowY: "auto", padding: "0 8px 28px", flex: 1, WebkitOverflowScrolling: "touch" }}>{children}</div>
      </div>
    </div>,
    document.body
  );
};

export const rowButtonStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "13px 12px",
  border: "none",
  background: "transparent",
  borderRadius: 12,
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "inherit",
};

export const PickerCheckIcon = CheckIcon;

export const EmptyRow = ({ text }) => (
  <div style={{ padding: "28px 16px", textAlign: "center", fontSize: 14, color: "#94A3B8" }}>{text}</div>
);
export const LoadingRow = ({ text = "Laster…" }) => (
  <div style={{ padding: "28px 16px", textAlign: "center", fontSize: 14, color: "#94A3B8" }}>{text}</div>
);

const dedupeById = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    if (!row?.id || seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
};

const mapProjectRow = (r) => ({
  id: r.id,
  name: r.displayTitle ?? r.name ?? "",
  client: r?.customer?.displayTitle ?? "",
  object: r,
});

const projectMatchesQuery = (row, q) => {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const obj = row.object;
  return [
    row.name,
    row.client,
    obj?.name,
    obj?.project_number,
    obj?.parent_project?.displayTitle,
    obj?.parent_project?.name,
    obj?.parent_project?.project_number,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
};

const sectionHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#64748B",
  background: "#F8FAFC",
  borderTop: "1px solid #F1F5F9",
  borderBottom: "1px solid #F1F5F9",
};

/** Project picker — searchable bottom sheet with colour dot + client + suggestions. */
export const ProjectPickerSheet = ({ open, onClose, selectedId, onSelect, title, hint = null }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [suggestedItems, setSuggestedItems] = useState([]);
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await axios.get("/projects/", {
        params: {
          query: q || undefined,
          page: 1,
          sortBy: "created_at",
          sort: "desc",
          fields: "*",
          include_suggested: true,
          offer_scope: "all",
        },
      });
      const projectRows = Array.isArray(res.data?.data) ? res.data.data : [];
      const suggestedRows = Array.isArray(res.data?.suggestedProjects) ? res.data.suggestedProjects : [];
      const favoriteRows = Array.isArray(
        res.data?.favoriteProjects ?? res.data?.favorite_projects ?? res.data?.favorites
      )
        ? res.data?.favoriteProjects ?? res.data?.favorite_projects ?? res.data?.favorites
        : [];

      setFavoriteItems(dedupeById(favoriteRows).map(mapProjectRow));
      setSuggestedItems(dedupeById(suggestedRows).map(mapProjectRow));
      setItems(dedupeById(projectRows).map(mapProjectRow));
    } catch {
      setItems([]);
      setSuggestedItems([]);
      setFavoriteItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debounced = useRef(debounce((q) => fetchRows(q), 300)).current;
  useEffect(() => {
    if (!open) return undefined;
    debounced(query);
    return () => debounced.cancel();
  }, [query, open, debounced]);
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const visibleSections = useMemo(() => {
    const favorites = favoriteItems.filter((row) => projectMatchesQuery(row, query));
    const favoriteIds = new Set(favorites.map((row) => row.id));
    const suggested = suggestedItems
      .filter((row) => projectMatchesQuery(row, query))
      .filter((row) => !favoriteIds.has(row.id));
    const promotedIds = new Set([
      ...favorites.map((row) => row.id),
      ...suggested.map((row) => row.id),
    ]);
    const projects = items
      .filter((row) => projectMatchesQuery(row, query))
      .filter((row) => !promotedIds.has(row.id));

    return [
      {
        key: "favorites",
        title: t("project_selector.favorites"),
        icon: "star",
        rows: favorites,
      },
      {
        key: "suggested",
        title: t("project_selector.suggested"),
        icon: "clock",
        rows: suggested,
      },
      {
        key: "projects",
        title: query.trim() ? t("project_selector.search_results") : t("project_selector.projects"),
        icon: null,
        rows: projects,
      },
    ].filter((section) => section.rows.length > 0);
  }, [favoriteItems, items, query, suggestedItems, t]);

  const allVisibleRows = useMemo(
    () => visibleSections.flatMap((section) => section.rows),
    [visibleSections]
  );

  const renderProjectRow = (p) => {
    const isSel = String(p.id) === String(selectedId);
    const obj = p.object;
    const isSub = !!(obj?.parent_project_id || obj?.parent_project);
    const isOffer = obj?.is_offer === true;
    return (
      <button
        key={p.id}
        type="button"
        style={{
          ...rowButtonStyle,
          ...(isSel ? { background: ACCENT.soft } : {}),
        }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          onSelect(p.object);
          onClose();
        }}
      >
        <span
          style={{
            width: 13,
            height: 13,
            borderRadius: 4,
            flex: "0 0 auto",
            background: avatarColor(p.id || p.name),
            ...(isSub ? { marginLeft: 6 } : {}),
          }}
        />
        <span style={{ flex: 1, minWidth: 0, ...(isSub ? { borderLeft: "2px solid #E2E8F0", paddingLeft: 10 } : {}) }}>
          <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{p.name}</span>
            {isOffer ? (
              <span
                style={{
                  display: "inline-flex",
                  borderRadius: 999,
                  background: "#FEF3C7",
                  padding: "2px 8px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#92400E",
                }}
              >
                {t("project_selector.offer")}
              </span>
            ) : null}
          </span>
          {p.client ? (
            <span style={{ display: "block", fontSize: 13, color: "#94A3B8", fontWeight: 500 }}>{p.client}</span>
          ) : null}
          {obj?.parent_project ? (
            <span style={{ display: "block", fontSize: 12, color: "#64748B", fontWeight: 500, marginTop: 2 }}>
              {t("project_selector.sub_of")}:{" "}
              {obj.parent_project.displayTitle ||
                obj.parent_project.name ||
                obj.parent_project.project_number}
            </span>
          ) : null}
        </span>
        {isSel && <CheckIcon />}
      </button>
    );
  };

  const sectionIcon = (kind) => {
    if (kind === "star") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l3.09 6.26L20 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l4.91-1.01L12 2z" />
        </svg>
      );
    }
    if (kind === "clock") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      );
    }
    return null;
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title || "Velg prosjekt"}
      searchValue={query}
      onSearch={setQuery}
      searchPlaceholder="Søk prosjekt eller kunde"
    >
      {hint ? (
        <p style={{ margin: "0 10px 8px", fontSize: 13, color: "#64748B" }}>{hint}</p>
      ) : null}
      {loading ? (
        <LoadingRow />
      ) : allVisibleRows.length === 0 ? (
        <EmptyRow
          text={
            query.trim()
              ? t("project_selector.no_projects_found")
              : t("project_selector.no_projects_to_show")
          }
        />
      ) : (
        visibleSections.map((section) => (
          <div key={section.key}>
            <div style={sectionHeaderStyle}>
              {section.icon ? sectionIcon(section.icon) : null}
              <span>{section.title}</span>
            </div>
            {section.rows.map((p) => renderProjectRow(p))}
          </div>
        ))
      )}
    </BottomSheet>
  );
};

/** Employee picker — searchable bottom sheet with avatar initials. */
export const EmployeePickerSheet = ({ open, onClose, selectedId, onSelect }) => {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await axios.get("/users/", {
        params: {
          query: q || undefined,
          page: 1,
          per_page: 30,
          sortBy: "id",
          sort: "desc",
          filters: [{ name: "EMPLOYEE_OR_HIRED", value: true }],
          fields: "id,displayTitle",
          showInDropdown: true,
        },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data.map((r) => ({ id: r.id, name: r.displayTitle ?? "", object: r })));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debounced = useRef(debounce((q) => fetchRows(q), 300)).current;
  useEffect(() => {
    if (!open) return undefined;
    debounced(query);
    return () => debounced.cancel();
  }, [query, open, debounced]);
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <BottomSheet open={open} onClose={onClose} title="Velg ansatt" searchValue={query} onSearch={setQuery} searchPlaceholder="Søk ansatt">
      {loading ? (
        <LoadingRow />
      ) : rows.length === 0 ? (
        <EmptyRow text="Ingen ansatte funnet" />
      ) : (
        rows.map((e) => {
          const isSel = String(e.id) === String(selectedId);
          return (
            <button
              key={e.id}
              type="button"
              style={rowButtonStyle}
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => {
                onSelect(e.object);
                onClose();
              }}
            >
              <span style={{ width: 38, height: 38, borderRadius: 11, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 13, background: avatarColor(e.id || e.name) }}>
                {initialsOf(e.name)}
              </span>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{e.name}</span>
              {isSel && <CheckIcon />}
            </button>
          );
        })
      )}
    </BottomSheet>
  );
};

const activityLabelOf = (r) => r?.display_title ?? r?.displayTitle ?? r?.title ?? `#${r?.id}`;

/** Activity picker — bottom sheet listing a project's activities, with search. */
export const ActivityPickerSheet = ({ open, onClose, projectId, selectedId, onSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const { activities: rows, groups, loading } = useProjectActivityPickerData(projectId, {
    enabled: open,
  });

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const q = query.trim().toLowerCase();
  const matchesRow = (a) =>
    !q ||
    [activityLabelOf(a), a?.number, a?.activity_type, a?.group?.title]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));

  const showHeaders = hasRealActivityGroups(rows, groups);
  const sections = (() => {
    const built = buildActivityGroupSections(rows, groups, {
      ungroupedLabel: t("project_activity.ungrouped"),
      hideEmptyGroups: Boolean(q),
    });
    return q ? filterActivityGroupSections(built, matchesRow) : built;
  })();
  const visibleCount = sections.reduce((n, s) => n + s.activities.length, 0);

  const renderActivityButton = (a) => {
    const isSel = String(a.id) === String(selectedId);
    return (
      <button
        key={a.id}
        type="button"
        style={{
          ...rowButtonStyle,
          padding: showHeaders ? "12px 12px 12px 22px" : "14px 12px",
        }}
        onClick={() => {
          onSelect(a);
          onClose();
        }}
      >
        <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: "#0F172A" }}>{activityLabelOf(a)}</span>
        {isSel && <CheckIcon />}
      </button>
    );
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t("project_activity.dialog_title")}
      searchValue={query}
      onSearch={setQuery}
      searchPlaceholder={t("project_activity.search_placeholder")}
    >
      {loading ? (
        <LoadingRow />
      ) : visibleCount === 0 ? (
        <EmptyRow text={q ? t("project_activity.empty") : t("project_activity.empty")} />
      ) : showHeaders ? (
        sections.map((section) => (
          <div key={section.key}>
            <div
              style={{
                ...sectionHeaderStyle,
                color: "#0B3A78",
                background: "#EAF3FD",
                borderTop: "1px solid #BFDCF7",
                borderBottom: "1px solid #BFDCF7",
                justifyContent: "space-between",
              }}
            >
              <span>{section.title || t("project_activity.ungrouped")}</span>
              <span style={{ color: "#1463C7", fontSize: 11 }}>{section.activities.length}</span>
            </div>
            {section.activities.map(renderActivityButton)}
          </div>
        ))
      ) : (
        sections.flatMap((section) => section.activities).map(renderActivityButton)
      )}
    </BottomSheet>
  );
};
